import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IActionStatus } from 'src/app/shared/interfaces/action-status.interface';
import { ISchedulerSettings } from '../../interfaces/scheduler-settings.interface';
import { SchedulerSettingsService } from '../../services/scheduler-settings.service';
import * as _ from 'lodash';
import { MatDialogRef } from '@angular/material/dialog';
import { ProcessService } from 'src/app/modules/instance/services';

const MAX_PRS_BUNCH_SIZE_ALLOWED = 20;

@Component({
    standalone: false,
    selector: 'app-scheduler-settings',
    templateUrl: './scheduler-settings.component.html',
    styleUrls: ['./scheduler-settings.component.scss']
})
export class SchedulerSettingsComponent implements OnInit, OnDestroy {
    @Input()
    hideHeader: boolean = false;

    schedulerSettings: ISchedulerSettings;

    get settings(): ISchedulerSettings {
        return this.schedulerSettings;
    }

    @Input()
    set settings(value: ISchedulerSettings) {
        this.schedulerSettings = value;
        if (this.schedulerSettings) {
            this.setFormData(this.schedulerSettings);
            this.showSpinner = false;
        }
    }

    @Input()
    useOuterSetting: boolean = false;

    readonly minCheckIntervalSeconds = 10;
    readonly minConcurrentProcesses = 1;
    readonly maxCheckIntervalSeconds = 86400;
    readonly maxConcurrentProcesses = 50;

    readonly minPauseDuration = 1;
    readonly maxPauseDuration = 96;

    readonly minThreadsCount = 1;
    readonly maxThreadsCount = 1000;

    readonly minPRsBunchSizeAllowed = 1;
    readonly maxPRsBunchSizeAllowed = MAX_PRS_BUNCH_SIZE_ALLOWED;

    showSpinner: boolean = true;

    saveActionState: IActionStatus = { isWaitingActionFinish: false };

    formGroup: FormGroup;
    turnOnScheduler = new FormControl(false);
    checkIntervalSecondsControl = new FormControl(30, [
        Validators.required,
        Validators.min(this.minCheckIntervalSeconds),
        Validators.max(this.maxCheckIntervalSeconds),
        Validators.pattern('^[0-9]*$')
    ]);
    maxConcurrentProcessesControl = new FormControl(1, [
        Validators.required,
        Validators.min(this.minConcurrentProcesses),
        Validators.max(this.maxConcurrentProcesses),
        Validators.pattern('^[0-9]*$')
    ]);

    pauseDurationControl = new FormControl(null, [
        Validators.min(this.minPauseDuration),
        Validators.max(this.maxPauseDuration),
        Validators.pattern('^[0-9]*$')
    ]);

    maxThreadsCountControl: FormControl;

    maxPRsBunchSizeAllowedControl = new FormControl(null, [
        Validators.min(this.minPRsBunchSizeAllowed),
        Validators.max(this.maxPRsBunchSizeAllowed),
        Validators.pattern('^[0-9]*$')
    ]);

    defaultPRsBunchSizeControl = new FormControl(null, [
        Validators.min(this.minPRsBunchSizeAllowed),
        Validators.pattern('^[0-9]*$')
    ]);

    subscriptions: Subscription[] = [];

    constructor(
        protected readonly formBuilder: FormBuilder,
        protected readonly schedulerSettingsService: SchedulerSettingsService,
        private dialogRef: MatDialogRef<SchedulerSettingsComponent>,
        private readonly processService: ProcessService
    ) {
        this.maxThreadsCountControl = new FormControl(null, [
            Validators.min(this.validatedMinThreadsCount),
            Validators.max(this.maxThreadsCount),
            Validators.pattern('^[0-9]*$')
        ]);

        this.formGroup = formBuilder.group(
            {
                turnOnScheduler: this.turnOnScheduler,
                checkIntervalSeconds: this.checkIntervalSecondsControl,
                maxConcurrentProcesses: this.maxConcurrentProcessesControl,
                newPauseDuration: this.pauseDurationControl,
                maxThreadsCount: this.maxThreadsCountControl,
                maxPRsBunchSizeAllowed: this.maxPRsBunchSizeAllowedControl,
                defaultMaxPRsBunchSize: this.defaultPRsBunchSizeControl
            },
            {
                validators: [defaultMaxPRsBunchSizeValidator]
            }
        );
    }

    get validatedMinThreadsCount(): number {
        return this.processService.runningThreadsCount || this.minThreadsCount;
    }

    get threadsCountPlaceholder(): string {
        return `${this.validatedMinThreadsCount} - ${this.maxThreadsCount} threads`;
    }

    get maxBunchSizePlaceholder(): string {
        return `${this.minPRsBunchSizeAllowed} - ${this.maxPRsBunchSizeAllowed} PRs`;
    }

    get maxDefaultBunchSize(): number {
        return _.defaultTo(_.get(this, 'formGroup.value.maxPRsBunchSizeAllowed'), this.maxPRsBunchSizeAllowed);
    }

    get defaultBunchSizePlaceholder(): string {
        return `${this.minPRsBunchSizeAllowed} - ${this.maxDefaultBunchSize} PRs`;
    }

    get pauseStartStrSafe(): string {
        return _.get(this.schedulerSettings, 'pauseStartStr');
    }

    get pauseEndStrSafe(): string {
        return _.get(this.schedulerSettings, 'pauseEndStr');
    }

    get pauseDurationSafeStr(): string {
        return String(_.get(this.schedulerSettings, 'pauseDuration', 'Checking...'));
    }

    ngOnInit() {
        if (!this.useOuterSetting) {
            this.subscriptions.push(
                this.schedulerSettingsService
                    .requestSchedulerSettings()
                    .pipe(filter(value => !!value))
                    .subscribe(scheduleSettings => {
                        this.schedulerSettings = scheduleSettings.clone();
                        this.showSpinner = false;
                        this.setFormData(scheduleSettings);
                    })
            );
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setFormData(scheduleSettings: ISchedulerSettings) {
        if (!scheduleSettings) {
            return;
        }
        this.formGroup.patchValue(scheduleSettings);
    }

    clearPause() {
        this.schedulerSettings.clearPause();
    }

    save() {
        this.schedulerSettings.initWithJSON(
            _.pick(this.formGroup.value, [
                'turnOnScheduler',
                'checkIntervalSeconds',
                'maxConcurrentProcesses',
                'maxThreadsCount',
                'maxPRsBunchSizeAllowed',
                'defaultMaxPRsBunchSize'
            ]) as any
        );
        this.dialogRef.close(this.schedulerSettings);
        this.schedulerSettings.setPauseDuration(this.formGroup.value.newPauseDuration);
    }

    handleTurnOnSliderChange() {
        this.turnOnScheduler.value ? this.pauseDurationControl.enable() : this.pauseDurationControl.disable();
        this.pauseDurationControl.setValue(this.turnOnScheduler.value ? this.pauseDurationControl.value : null);
    }
}

function defaultMaxPRsBunchSizeValidator(formGroup: FormGroup) {
    const maxValue = +formGroup.value.maxPRsBunchSizeAllowed || MAX_PRS_BUNCH_SIZE_ALLOWED;

    const result =
        maxValue < +formGroup.value.defaultMaxPRsBunchSize
            ? {
                  defaultMaxPRsBunchSizeMax: true
              }
            : null;

    if (result) {
        formGroup.controls.defaultMaxPRsBunchSize.setErrors({ max: true });
    } else if (formGroup.controls.defaultMaxPRsBunchSize.errors) {
        delete formGroup.controls.defaultMaxPRsBunchSize.errors['max'];
        formGroup.controls.defaultMaxPRsBunchSize.markAsPending();
    }

    return result;
}
